import { type Ref, computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import {
  FORM_VALID,
  type Validator,
  makeValidate,
  matchingRegExp,
  maxLength,
  required,
} from '@ds/components/Form'

import {
  CAPACITY_OPTION_VALUES,
  CONDITIONAL_CATEGORIES,
  DESCRIPTION_MAX_LENGTH,
  EMAIL_REGEX,
  RISK_NATURE_OPTION_VALUES,
  STEP_FIELDS,
  URL_REGEX,
  YES_NO_UNKNOWN_OPTION_VALUES,
} from '../ReportIllicitContent.constants'
import type { ReportIllicitContentFormValues } from '../ReportIllicitContent.types'

import translations from './useReportIllicitContentForm.translations'

const CAPACITY_OPTION_LABELS: Record<
  (typeof CAPACITY_OPTION_VALUES)[number],
  keyof typeof translations
> = {
  individual: 'capacityOptionIndividual',
  company: 'capacityOptionCompany',
  authority: 'capacityOptionAuthority',
  trusted_flagger: 'capacityOptionTrustedFlagger',
  rights_holder: 'capacityOptionRightsHolder',
  other: 'capacityOptionOther',
}

const RISK_NATURE_OPTION_LABELS: Record<
  (typeof RISK_NATURE_OPTION_VALUES)[number],
  keyof typeof translations
> = {
  fire: 'riskNatureOptionFire',
  electric_shock: 'riskNatureOptionElectricShock',
  suffocation: 'riskNatureOptionSuffocation',
  chemical: 'riskNatureOptionChemical',
  injury: 'riskNatureOptionInjury',
  other: 'riskNatureOptionOther',
}

const YES_NO_UNKNOWN_OPTION_LABELS: Record<
  (typeof YES_NO_UNKNOWN_OPTION_VALUES)[number],
  keyof typeof translations
> = {
  yes: 'yesNoUnknownOptionYes',
  no: 'yesNoUnknownOptionNo',
  unknown: 'yesNoUnknownOptionUnknown',
}

function requiredWhenConditional(
  message: string,
): Validator<ReportIllicitContentFormValues> {
  return (value, values) => {
    if (!CONDITIONAL_CATEGORIES.includes(values.category as never))
      return FORM_VALID

    return isEmpty(value) ? message : FORM_VALID
  }
}

function mustBeChecked(
  message: string,
): Validator<ReportIllicitContentFormValues> {
  return (value) => (value === true ? FORM_VALID : message)
}

export function useReportIllicitContentForm(currentStep: Ref<string>) {
  const i18n = useI18n()

  const initialValues: ReportIllicitContentFormValues = {
    category: '',
    description: '',
    contentUrl: '',
    reporterName: '',
    reporterEmail: '',
    capacity: '',
    country: '',
    riskNature: '',
    hasCausedIncident: '',
    isSubjectToRecall: '',
    goodFaithCertified: false,
    isTrustedFlagger: false,
  }

  const capacityOptions = computed(() =>
    CAPACITY_OPTION_VALUES.map((value) => ({
      value,
      label: i18n(translations[CAPACITY_OPTION_LABELS[value]]),
    })),
  )

  const riskNatureOptions = computed(() =>
    RISK_NATURE_OPTION_VALUES.map((value) => ({
      value,
      label: i18n(translations[RISK_NATURE_OPTION_LABELS[value]]),
    })),
  )

  const yesNoUnknownOptions = YES_NO_UNKNOWN_OPTION_VALUES.map((value) => ({
    value,
    label: i18n(translations[YES_NO_UNKNOWN_OPTION_LABELS[value]]),
  }))

  function whenStep(
    field: keyof ReportIllicitContentFormValues,
    validators:
      | Validator<ReportIllicitContentFormValues>
      | Array<Validator<ReportIllicitContentFormValues>>,
  ): Validator<ReportIllicitContentFormValues> {
    const list = Array.isArray(validators) ? validators : [validators]

    return (value, values, context) => {
      if (
        !STEP_FIELDS[currentStep.value as keyof typeof STEP_FIELDS].includes(
          field,
        )
      )
        return FORM_VALID

      for (const validator of list) {
        const result = validator(value, values, context)

        if (result !== FORM_VALID) return result
      }

      return FORM_VALID
    }
  }

  const validate = makeValidate<ReportIllicitContentFormValues>({
    category: whenStep(
      'category',
      required(i18n(translations.categoryErrorRequired)),
    ),
    description: whenStep('description', [
      required(i18n(translations.descriptionErrorRequired)),
      maxLength(
        DESCRIPTION_MAX_LENGTH,
        i18n(translations.descriptionErrorMaxLength, {
          maxLength: DESCRIPTION_MAX_LENGTH,
        }),
      ),
    ]),
    contentUrl: whenStep('contentUrl', [
      required(i18n(translations.contentUrlErrorRequired)),
      matchingRegExp(URL_REGEX, i18n(translations.contentUrlErrorInvalid)),
    ]),
    reporterName: whenStep(
      'reporterName',
      required(i18n(translations.reporterNameErrorRequired)),
    ),
    reporterEmail: whenStep('reporterEmail', [
      required(i18n(translations.reporterEmailErrorRequired)),
      matchingRegExp(EMAIL_REGEX, i18n(translations.reporterEmailErrorInvalid)),
    ]),
    capacity: whenStep(
      'capacity',
      required(i18n(translations.capacityErrorRequired)),
    ),
    country: whenStep(
      'country',
      required(i18n(translations.countryErrorRequired)),
    ),
    riskNature: whenStep(
      'riskNature',
      requiredWhenConditional(i18n(translations.riskNatureErrorRequired)),
    ),
    hasCausedIncident: whenStep(
      'hasCausedIncident',
      requiredWhenConditional(
        i18n(translations.hasCausedIncidentErrorRequired),
      ),
    ),
    isSubjectToRecall: whenStep(
      'isSubjectToRecall',
      requiredWhenConditional(
        i18n(translations.isSubjectToRecallErrorRequired),
      ),
    ),
    goodFaithCertified: whenStep(
      'goodFaithCertified',
      mustBeChecked(i18n(translations.goodFaithCertifiedErrorMustBeChecked)),
    ),
  })

  return {
    initialValues,
    validate,
    capacityOptions,
    riskNatureOptions,
    yesNoUnknownOptions,
  }
}
